import { DateTime } from 'luxon';
import { OrderReturnStatus, OrderStatus, OrderType } from '../../quipli-service/order/order';
import { CustomerProductParsers } from '../product/customer-product-parsers';
import { CustomerOrder, CustomerOrderProduct } from './customer-order';
import { CustomerInvoiceParsers } from '../invoice/customer-invoice-parsers';
import { OrderParsers } from '../../quipli-service/order/order-parsers';
import { UserAddress } from '../../quipli-service/user/user';
import { PaymentParsers } from '../../quipli-service/payment/payment-parsers';
import CustomerProductJson = CustomerProductParsers.CustomerProductJson;
import { TaxBreakdownJson, toTaxBreakdown } from '../../quipli-service/tax/tax';

export namespace CustomerOrderParsers {
  import CustomerInvoiceJson = CustomerInvoiceParsers.CustomerInvoiceJson;
  import PaymentMethodJson = PaymentParsers.PaymentMethodJson;
  export type CustomerOrderJson = {
    id: number;
    orderNumber: string;
    locationId: number;
    dateAdded: string;
    quoteExpiresAt?: string;
    status: OrderStatus;
    returnStatus: OrderReturnStatus;
    products: CustomerOrderProductJson[];
    invoices: CustomerInvoiceJson[];
    isCycleBilled?: boolean;
    signature?: string;
    billingAddress?: UserAddress;
    shippingAddress?: UserAddress;
    additionalFees: OrderParsers.OrderAdditionalFeeJson[];
    damageWaiver?: number;
    additionalDiscount?: number;
    shippingCharged?: number;
    pickupCharged?: number;
    taxPercentage: number;
    taxCharged?: number;
    discountTotal?: number;
    discountCoupon?: string;
    rentalSecurity: number;
    rentalSecurityPercentage: number;
    netCharged: number;
    paymentMethod: PaymentMethodJson;
    taxBreakdown?: TaxBreakdownJson[];
  };

  export type CustomerOrderProductJson = {
    id: number;
    orderId: number;
    type: OrderType;
    product: CustomerProductJson;
    quantity: number;
    rentalStartDate: string;
    rentalEndDate?: string;
    actualRentalStartDate?: string;
    actualRentalEndDate?: string;
    additionalFees: OrderParsers.OrderProductAdditionalFeeJson[];
    customerBuyingPrice: number;
    discountPercentage?: number;
    isTaxFree: boolean;
    tax: number;
    name: string;
    description?: string;
  };

  export function toCustomerOrder(input: CustomerOrderJson): CustomerOrder {
    return {
      id: input.id,
      orderNumber: input.orderNumber,
      locationId: input.locationId,
      dateAdded: DateTime.fromISO(input.dateAdded, { setZone: true }),
      quoteExpiresAt: input.quoteExpiresAt ? DateTime.fromISO(input.quoteExpiresAt, { setZone: true }) : undefined,
      status: input.status,
      returnStatus: input.returnStatus,
      products: input.products.map(toCustomerOrderProduct),
      invoices: input.invoices.map(CustomerInvoiceParsers.toCustomerInvoice),
      signature: input.signature,
      billingAddress: input.billingAddress,
      shippingAddress: input.shippingAddress,
      additionalFees: input.additionalFees.map(OrderParsers.toOrderAdditionalFees),
      damageWaiver: input.damageWaiver,
      additionalDiscount: input.additionalDiscount,
      shippingCharged: input.shippingCharged,
      pickupCharged: input.pickupCharged,
      taxPercentage: input.taxPercentage,
      taxCharged: input.taxCharged,
      discountTotal: input.discountTotal,
      discountCoupon: input.discountCoupon,
      rentalSecurity: input.rentalSecurity,
      rentalSecurityPercentage: input.rentalSecurityPercentage,
      netCharged: input.netCharged,
      paymentMethod: PaymentParsers.toPaymentMethod(input.paymentMethod),
      taxBreakdown: input.taxBreakdown?.map(toTaxBreakdown),
    };
  }

  export function toCustomerOrderProduct(input: CustomerOrderProductJson): CustomerOrderProduct {
    return {
      id: input.id,
      orderId: input.orderId,
      type: input.type,
      product: CustomerProductParsers.toCustomerProduct(input.product),
      quantity: input.quantity,
      rentalStartDate: DateTime.fromISO(input.rentalStartDate, { setZone: true }),
      rentalEndDate: input.rentalEndDate ? DateTime.fromISO(input.rentalEndDate, { setZone: true }) : undefined,
      actualRentalStartDate: input.actualRentalStartDate
        ? DateTime.fromISO(input.actualRentalStartDate, { setZone: true })
        : undefined,
      actualRentalEndDate: input.actualRentalEndDate
        ? DateTime.fromISO(input.actualRentalEndDate, { setZone: true })
        : undefined,
      additionalFees: input.additionalFees.map(OrderParsers.toOrderProductFees),
      customerBuyingPrice: input.customerBuyingPrice,
      discountPercentage: input.discountPercentage,
      isTaxFree: input.isTaxFree,
      tax: input.tax,
      name: input.name,
      description: input.description,
    };
  }
}
